import {ErrorHandler, Injectable, Injector} from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {Router} from '@angular/router';
import {DipRegistrationErrorInformation} from './models/dip-registration-error-information.model';
import {DipRegistrationAlertStore} from '../core/services/alert/dip-registration-alert-store.service';
import {DipRegistrationLoggerService} from '../core/services/logger/dip-registration-logger.service';
import {joinObjectKeyValues} from '../core/services/utilities/dip-registration-utility';
import {
  DipRegistrationContextService
} from '../core/services/dip-registration-context.service';
import { DipRegistrationLoggingNames } from '../core/enum/dip-registration-logging-names.enum';
import { DiprsSpaLoggerService } from '../core/services/logger/diprs-spa-logger.service';
import { LoggerEventEnum } from '../core/models/dip-registration-log-information.model';

@Injectable()
export class DipRegistrationErrorsHandler implements ErrorHandler {

  constructor(private injector: Injector,
              private logger: DipRegistrationLoggerService,
              private dipRegistrationAlertStore: DipRegistrationAlertStore,
              private dipRegistrationContextService: DipRegistrationContextService,
              private spaLoggerService: DiprsSpaLoggerService) {
  }

  public getErrorCodeAndMessage(errorInformation: DipRegistrationErrorInformation): { errorCode: number, errorMessage: string } {
    const result = {errorCode: null, errorMessage: null};
    if (errorInformation?.status?.additionalStatus?.length) {
      const errorObj = errorInformation.status.additionalStatus.find(additionalStatus => additionalStatus.statusCode >= 1000 && additionalStatus.statusCode < 2000);
      if (errorObj) {
        result.errorCode = errorObj.statusCode;
        result.errorMessage = errorObj.statusDesc;
      }
    }
    return result;
  }

  private async manage403ErrorCodes(router: Router, errorCode: number, errorMessage: string) {
    if (errorCode) {
      switch (errorCode) {
        case 1006 :   // no cif match found
          this.dipRegistrationAlertStore.updateAlertState({
            isEnabled: true,
            type: 'warning',
            messageKey: 'DIPRSRegistration.alert.not.match.found.message',
            titleKey: 'DIPRSRegistration.alert.not.match.found.title'
          });
          break;
        case 1040 :   // Party is a Secondary Account Holder
        case 1019 :   // Party has no account at all
        case 1014 :   // Party is not principal on any account
        case 1053 :   // Party has no eligible role
        case 1054 :   // Party only have an eligible role without consent on any of the associated accounts
          this.dipRegistrationAlertStore.updateAlertState({
            isEnabled: true,
            type: 'warning',
            messageKey: 'DIPRSRegistration.alert.principal.account.message',
            titleKey: 'DIPRSRegistration.alert.principal.account.title'
          });
          break;

        case 1035 : // Bad CSRF token
          await router.navigate(['/applicationError'], {queryParams: {errorCode, type: 'technical'}});
          break;
        // case 1021:  filed validation errors
        // case 1022 : Party is not an individual, they are a business
        // case 1025 : Party is already enrolled to MyInsurance
        // case 1023 : Party is deceased
        // case 1015 : Multiple parties match search criteria
        // case 1033 : Party has no active policies
        // case 1024 : User has reached their maximum retry limit on search
        // case 1034:  Party is missing key elements in CIF
        // case 1038:  Too many party matches
        // case 1039:  Too many accounts
        // case 1041:  A required prior call has not been made
        // case 1062: ConnectID found is used for MyI and other TD Products
        default:
          await router.navigate(['/applicationError'], {queryParams: {errorCode, type: 'functional'}});
          break;
      }
    } else {
      this.logger.error('unexpected error code is when managing 403', errorCode, errorMessage);
      await router.navigate(['/applicationError']);
    }
  }


  async handleError(error: Error | HttpErrorResponse) {
    if (error instanceof HttpErrorResponse) {
      // Server or connection error happened
      if (!navigator.onLine) {
        // Handle offline error
      } else {
        // if (this.injector) {   // in case we are using error handler in initializer service, injector is not initialized yet and it is undefined
        const router = this.injector.get(Router);
        // Handle Http Error (error.status === 403, 404...)
        if (error.status || error.status === 0) {
          this.dipRegistrationAlertStore.resetAlertState();
          const errorInformation = error.error ? error.error as DipRegistrationErrorInformation : null;
          const {errorCode, errorMessage} = this.getErrorCodeAndMessage(errorInformation);
          const isAutoEnrollFlow = this.dipRegistrationContextService.getIsAutoEnrollFlow();
          if (isAutoEnrollFlow && error.status >= 400 && error.status < 500) {
            await router.navigate(['customer-identification'], this.dipRegistrationContextService.buildParams());
          } else {
            switch (error.status) {
              case 404 :
                await router.navigate(['/404']);
                break;
              case 400 :
                // case 1021: Party information in request is not valid
                // case 1027: Invalid phone Id in request
                // case 1031: Missing auth code or redirect URI in request
                // case 2003: The x-environment-id contains an unknown environment identifier
                await router.navigate(['/applicationError'], {queryParams: {errorCode, type: 'technical'}});
                break;
              case 403: {
                this.manage403ErrorCodes(router, errorCode, errorMessage);
                break;
              }
              // case 1002:  calling NRL, could be either NRL is down or NRL returned an error
              // case 1003:  calling one of the Parties, Accounts or Policies APIs
              // case 1018:  calling CSSP API
              // case 1020:  calling Valet API
              // case 1008:  Pingfed server can't be reached
              // case 1009:  Bad Pingfed configuration
              // case 1010:  Pingfed returned an invalid token (very unlikely to happen)
              // case 1017:  Pingfed token exchange error, probable cause is bad authorization code
              // case 1016:  service availability
              // case 1020:  Error calling Valet API
              // case 1028:  Valet and/or CSSP exception
              // case 1029:  Problem building JSON payload for Valet (very unlikely to happen)
              // case 2001:  Exception caught
              // case 2002:  A required configuration property is missing from the config files. That can happen if the x-environment-id header is not set properly
              // case 2004:  A property does not have the expected format
              // case 2005:  The generic error handler received an unknown error code and thus can't return a description for it.
              // case 2007:  One property for vault configuration has an invalid format, must be VAULT::.+::.+ for Hashicorp and VAULT::.+::.+::.+ for JBoss
              // case 2008:  System error accessing vault (Tivoli alert trigger)
              // case 1032:  error while getting availability status
              default:
                await router.navigate(['/applicationError'], {queryParams: {errorCode, type: 'technical'}});
                break;
            }
          }
        } else {
          this.logger.error(error);
          await router.navigate(['/applicationError']);
        }
      }
    } else {
      // call log Api
      this.log(error);
    }
  }

  handleMaintenancePageRedirection(errorCode?: string, appVersion?: string, sid?: string) {
    const maintenanceUrl = this.dipRegistrationContextService.getMaintenancePageUrl();
    const queryParamsString = joinObjectKeyValues({
      errorCode,
      sid,
      appVersion
    }, '&');
    window.location.href = queryParamsString ? `${maintenanceUrl}?${queryParamsString}` : maintenanceUrl;
  }

  log(error) {
    if (error) {
      const http = this.injector.get(HttpClient);
      // Log the error to the console
      this.logger.error(error);
      // Send error to server
      this.spaLoggerService.error(LoggerEventEnum.ERROR, DipRegistrationLoggingNames.CUSTOMER_IDENTIFICATION, { errorStackTrace: error});
    }
  }
}
